import { format } from "date-fns";
import { DeviceTypesEnum } from "../../common/enums/DeviceTypesEnum";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { valueOrDefault } from "../../common/utilities/ValueOrDefault";
import { AttributeSchemaTypeEnum, IAttribute } from "../../services/attribute-data-service/Models";
import { AcmObjectTypeEnum, IAcmDeviceProtocolObjectUsage, IAcmProtocolObject } from "../../services/comm-data-service/Models";
import { IObjectPropertiesDeviceModel, IObjectPropertiesTankBatteryModel, IObjectPropertiesTankModel, IObjectPropertiesWellModel, IObjectPropertyListModel } from "../../services/properties-data-service/Models";
import _ from "lodash";
import { getTimeframeTimeDisplayText } from "../../common/utilities/TimeframeUtilities";
import { TimeframeTypeEnum } from "../../common/enums/TimeframeTypeEnum";

export type CommDisplayInfo = {
    name: string;
    ip: string;
    port?: number;
};

export type ObjectPropertiesFormValues = {
    name?: string;
    phoneticName?: string | null;
    //deviceTypeID?: number;
    deviceTypeID: string;   // this must be a string; otherwise mui will report errors unless there is actually a devicetypeid = 0 (default value)
    identifier?: string | null;
    validateGasAnalysis?: boolean;

    validateGasAnalysisText?: string;

    //padID: number;
    padName?: string;
    //fieldID: number;
    fieldName?: string;
    //basinID: number;
    basinName?: string;
    apiID?: string;
    //TODO: Current app doesn't correcly reflect this. lift type and lift controller are a pair.    
    //liftTypeID: number;
    liftTypeName?: string;
    //liftControllerID: number;
    liftControllerName?: string;
    //resourceTypeID: number;
    resourceTypeName?: string;

    //commEndpointType?: number;
    commEndpointType: string;
    commEndpointAddress?: string;
    commAddressUpdated?: string;
    commEndpointID?: string;
    commEndpointSource?: string;
    commEndpointUpdated?: string;
    iccid?: string;
    cycleToDateUsage?: string;

    deviceLocation?: string | null;
    latitude?: string;
    longitude?: string;

    levelTransmitterTypeID?: number;
    //levelTransmitterTypeName?: string;
    styleID?: number;
    styleName?: string;

    height?: string;
    barrelsPerInch?: string;
    capacity?: string;

    excludeBOPD?: boolean;
    excludeBWPD?: boolean;

    pollingScheduleID?: number;
    pollingScheduleName: string;

    resourceUnitsPerInch?: string;


    externalContentID?: number;
    externalContentTitle?: string;
    externalContentUrl?: string;

    attributes: IAttribute[];
};

export const defaultValues: ObjectPropertiesFormValues = {
    name: "",
    phoneticName: "",
    deviceTypeID: '',
    identifier: "",
    validateGasAnalysis: false,

    //padID: number;
    padName: "",
    //fieldID: number;
    fieldName: "",
    //basinID: number;
    basinName: "",
    apiID: "",
    //TODO: Current app doesn't correcly reflect this. lift type and lift controller are a pair.    
    //liftTypeID: number;
    liftTypeName: "",
    //liftControllerID: number;
    liftControllerName: "",
    //resourceTypeID: number;
    resourceTypeName: "",


    //commEndpointType: 0,
    commEndpointType: '',
    commEndpointAddress: "",
    commAddressUpdated: "",
    commEndpointID: "",
    commEndpointSource: "",
    commEndpointUpdated: "",
    iccid: "",
    cycleToDateUsage: "",

    deviceLocation: "",
    latitude: "",
    longitude: "",

    levelTransmitterTypeID: 0,
    //levelTransmitterTypeName?: string;
    styleID: 0,
    // styleName?: string;

    height: "",
    barrelsPerInch: "",
    capacity: "",

    excludeBOPD: false,
    excludeBWPD: false,

    pollingScheduleID: 0,
    pollingScheduleName: '',

    resourceUnitsPerInch: '',
    externalContentTitle: '',
    externalContentUrl: '',
    externalContentID: -1,

    attributes: []
};

// function getPollingScheduleID(model: IObjectPropertiesDeviceModel): number {
//     const ids = model.tagPollingSchedules.map(sched => sched.scheduleID);
//     const uniqueIDs = ids.filter((value, index, self) => self.indexOf(value) === index);
//     return uniqueIDs.length > 1 ? 0 : uniqueIDs[0];
// }

export function getPollingScheduleName(model: IObjectPropertiesDeviceModel, propertLists: IObjectPropertyListModel): string {
    const ids = model.tagPollingSchedules.map(sched => sched.scheduleID);
    const uniqueIDs = ids.filter((value, index, self) => self.indexOf(value) === index);
    return uniqueIDs.length > 1 ? 
        'Custom Per Tag' : 
        propertLists.tagPollingSchedules.find(o => o.id === model.tagPollingSchedules.at(0)?.scheduleID)?.description ?? '';
}



export function mapToFormAttributes(attrs: IAttribute[]): IAttribute[] {
    return attrs.map(o => {
        const copy = {...o};
        if (copy.attrs?.stype === AttributeSchemaTypeEnum.Date) {
            copy.avdate = new Date(copy.av);
        }
        return copy;
    })
}

export function mapToDeviceFormValues(model: IObjectPropertiesDeviceModel, attrs: IAttribute[], propertLists: IObjectPropertyListModel): ObjectPropertiesFormValues {

    const externalContent = (model.externalContent?.length ?? 0) > 0 ? model.externalContent[0] : undefined;
    return {
        ...defaultValues,
        name: valueOrDefault(model.name, defaultValues.name),
        phoneticName: valueOrDefault(model.phoneticName, defaultValues.phoneticName),
        deviceTypeID: valueOrDefault(model.deviceType.id, +defaultValues.deviceTypeID).toString(),
        identifier: valueOrDefault(model.identifier, defaultValues.identifier),
        validateGasAnalysis: valueOrDefault(model.deviceValidateGasAnalysis, defaultValues.validateGasAnalysis),
        commEndpointType: valueOrDefault(model.commEndpointType, +defaultValues.commEndpointType).toString(),
        commEndpointAddress: valueOrDefault(model.commEndpointAddress, defaultValues.commEndpointAddress),
        commAddressUpdated: model.commAddressLastChanged?.length > 0
            ? format(new Date(model.commAddressLastChanged), "P pp")
            : defaultValues.commAddressUpdated,
        commEndpointID: valueOrDefault(model.commEndpointID, defaultValues.commEndpointID),
        commEndpointSource: valueOrDefault(model.commEndpointSource, defaultValues.commEndpointSource),
        commEndpointUpdated: model.commEndpointLastUpdate?.length > 0
            ? format(new Date(model.commEndpointLastUpdate), "P pp")
            : defaultValues.commEndpointUpdated,
        iccid: valueOrDefault(model.iccid, defaultValues.iccid),
        cycleToDateUsage: valueOrDefault(model.cycleToDateUsage, defaultValues.cycleToDateUsage),
        deviceLocation: valueOrDefault(model.deviceLocation, defaultValues.deviceLocation),
        latitude: valueOrDefault(model.latitude, defaultValues.latitude),
        longitude: valueOrDefault(model.longitude, defaultValues.longitude),
        //pollingScheduleID: getPollingScheduleID(model),
        pollingScheduleName: getPollingScheduleName(model, propertLists),

        styleID: valueOrDefault(model.styleID, defaultValues.styleID),
        // styleName?: string;
        height: valueOrDefault(model.height, defaultValues.height),
        resourceUnitsPerInch: valueOrDefault(model.resourceUnitsPerInch, defaultValues.resourceUnitsPerInch),
        capacity: valueOrDefault(model.capacity, defaultValues.capacity),

        externalContentTitle: externalContent?.title  ?? '',
        externalContentUrl: externalContent?.url ?? '',
        externalContentID: externalContent?.id ?? -1,

        attributes: mapToFormAttributes(attrs)
    };
}

export function mapToTankFormValues(model: IObjectPropertiesTankModel, attrs: IAttribute[], propertLists: IObjectPropertyListModel): ObjectPropertiesFormValues {

    return {
        ...mapToDeviceFormValues(model, attrs, propertLists),
        levelTransmitterTypeID: valueOrDefault(model.levelTransmitterTypeID, defaultValues.levelTransmitterTypeID),
        //levelTransmitterTypeName?: string;
        excludeBOPD: valueOrDefault(model.excludeBOPD, defaultValues.excludeBOPD),
        excludeBWPD: valueOrDefault(model.excludeBWPD, defaultValues.excludeBWPD),
        barrelsPerInch: valueOrDefault(model.barrelsPerInch, defaultValues.barrelsPerInch),
    };
}

export function mapToTankBatteryFormValues(model: IObjectPropertiesTankBatteryModel, attrs: IAttribute[]): ObjectPropertiesFormValues {
    const externalContent = model.externalContent.length > 0 ? model.externalContent[0] : undefined;
    return {
        ...defaultValues,
        name: valueOrDefault(model.name, defaultValues.name),
        phoneticName: valueOrDefault(model.phoneticName, defaultValues.phoneticName),
        identifier: valueOrDefault(model.identifier, defaultValues.identifier),
        latitude: valueOrDefault(model.latitude, defaultValues.latitude),
        longitude: valueOrDefault(model.longitude, defaultValues.longitude),
        externalContentTitle: externalContent?.title  ?? '',
        externalContentUrl: externalContent?.url ?? '',
        externalContentID: externalContent?.id ?? -1,

        attributes: mapToFormAttributes(attrs)
    };
}

export function mapToWellFormValues(model: IObjectPropertiesWellModel, attrs: IAttribute[]): ObjectPropertiesFormValues {
    const externalContent = model.externalContent.length > 0 ? model.externalContent[0] : undefined;
    return {
        ...defaultValues,
        name: valueOrDefault(model.name, defaultValues.name),
        //padID: number;
        padName: valueOrDefault(model.padName, defaultValues.padName),
        //fieldID: number;
        fieldName: valueOrDefault(model.fieldName, defaultValues.fieldName),
        //basinID: number;
        basinName: valueOrDefault(model.basinName, defaultValues.basinName),
        apiID: valueOrDefault(model.apiID, defaultValues.apiID),
        //TODO: Current app doesn't correcly reflect this. lift type and lift controller are a pair.    
        //liftTypeID: number;
        liftTypeName: valueOrDefault(model.liftTypeName, defaultValues.liftTypeName),
        //liftControllerID: number;
        liftControllerName: valueOrDefault(model.liftControllerName, defaultValues.liftControllerName),
        //resourceTypeID: number;
        resourceTypeName: valueOrDefault(model.resourceTypeName, defaultValues.resourceTypeName),
        latitude: valueOrDefault(model.latitude, defaultValues.latitude),
        longitude: valueOrDefault(model.longitude, defaultValues.longitude),
        externalContentTitle: externalContent?.title  ?? '',
        externalContentUrl: externalContent?.url ?? '',
        externalContentID: externalContent?.id ?? -1,

        attributes: mapToFormAttributes(attrs)
    };
}

export function mapToAttributes(formAttributes: IAttribute[], modelAttributes: IAttribute[]): IAttribute[] {

    const addedAttributes = formAttributes.filter(o => o.aid < 0);
    let updatedAttributes = _.cloneDeep(modelAttributes)?.concat(addedAttributes.map(o => {
        return {
            ...o,
            added: true
        } as IAttribute
    }));

    updatedAttributes?.forEach(o => {
        const formAttribute = formAttributes.find(v => v.aid === o.aid);
        if (formAttribute === undefined) {
            o.deleted = true;
        } else if (o.attrs?.stype === AttributeSchemaTypeEnum.Date &&
            formAttribute.avdate &&
            !isNaN(formAttribute.avdate.getTime()) &&
            formAttribute.avdate?.getTime() !== new Date(o.av).getTime()) {
            o.av = getTimeframeTimeDisplayText(TimeframeTypeEnum.Daily, formAttribute.avdate) ;
            o.updated = true;
        } else if (o.attrs?.stype !== AttributeSchemaTypeEnum.Date &&
            formAttribute.av !== o.av) {
            o.av = formAttribute.av.toString();
            o.updated = true;
        }
    });

    updatedAttributes = updatedAttributes?.filter(o => o.added || o.deleted || o.updated) ?? [];

    return updatedAttributes;
}

export function mapToProperties(data: IObjectPropertiesDeviceModel | 
    IObjectPropertiesTankModel | 
    IObjectPropertiesTankBatteryModel | 
    IObjectPropertiesWellModel, 
    objectType: eLynxObjectTypeEnum,
    formData: ObjectPropertiesFormValues,
    propertyLists: IObjectPropertyListModel): IObjectPropertiesDeviceModel |
    IObjectPropertiesTankModel |
    IObjectPropertiesTankBatteryModel |
    IObjectPropertiesWellModel {

    const returnData = _.cloneDeep(data);

    //const returnData = {...data,
    //    name: formData.name as string,
    //    phoneticName: formData.phoneticName === '' ? null : formData.phoneticName as string,
    //};
    returnData.name = formData.name as string;
    returnData.phoneticName = formData.phoneticName === '' ? null : formData.phoneticName as string;


    switch(objectType) {
        case eLynxObjectTypeEnum.Device:
        case eLynxObjectTypeEnum.Tank:
            const deviceModel = returnData as IObjectPropertiesDeviceModel;

            //deviceModel.deviceType.id = formData.deviceTypeID as number;
            deviceModel.deviceType.id = +formData.deviceTypeID;
            deviceModel.identifier = formData.identifier === '' ? null : formData.identifier;
            deviceModel.deviceValidateGasAnalysis = formData.validateGasAnalysis ?? false;
            deviceModel.deviceLocation = formData.deviceLocation === '' ? null : formData.deviceLocation;
            deviceModel.latitude = formData.latitude;
            deviceModel.longitude = formData.longitude;

            if (objectType === eLynxObjectTypeEnum.Device) {

                const subType = propertyLists.deviceSubTypes.find(o => o.parentID === deviceModel.deviceType.id);

                if (subType) {
                    deviceModel.deviceSubType.id = subType.subTypeID;
                }

                if (deviceModel.deviceType.id === DeviceTypesEnum.StandaloneTank) {
                    deviceModel.styleID = formData.styleID;
                }
   
            }
            else if (objectType === eLynxObjectTypeEnum.Tank) {
                const tankModel = deviceModel as IObjectPropertiesTankModel;

                tankModel.levelTransmitterTypeID = formData.levelTransmitterTypeID;
                tankModel.styleID = formData.styleID;
                tankModel.excludeBOPD = formData.excludeBOPD;
                tankModel.excludeBWPD = formData.excludeBWPD;
            } 
            
            
            if (formData.externalContentTitle && formData.externalContentUrl &&
                formData.externalContentTitle.trim().length > 0 &&
                formData.externalContentUrl.trim().length > 0) {

                    deviceModel.externalContent = [{
                        id: formData.externalContentID ?? -1,
                        //createdBy: data.externalContent.length > 0 ? data.externalContent[0].createdBy : '',
                        title: formData.externalContentTitle.trim(),
                        url: formData.externalContentUrl.trim()
                    }];
            } else {
                deviceModel.externalContent = [];
            }

            return deviceModel;

        case eLynxObjectTypeEnum.TankBattery:
            const tankBatteryModel = returnData as IObjectPropertiesTankBatteryModel;
            tankBatteryModel.identifier = formData.identifier === '' ? null : formData.identifier;
            tankBatteryModel.latitude = formData.latitude;
            tankBatteryModel.longitude = formData.longitude;

            if (formData.externalContentTitle && formData.externalContentUrl &&
                formData.externalContentTitle.trim().length > 0 &&
                formData.externalContentUrl.trim().length > 0) {

                    tankBatteryModel.externalContent = [{
                        id: formData.externalContentID ?? -1,
                        createdBy: data.externalContent.length > 0 ? data.externalContent[0].createdBy : '',
                        title: formData.externalContentTitle.trim(),
                        url: formData.externalContentUrl.trim()
                    }];
            } else {
                tankBatteryModel.externalContent = [];
            }

            return tankBatteryModel;

        case eLynxObjectTypeEnum.Well:
            const wellModel = returnData as IObjectPropertiesWellModel;
            wellModel.latitude = formData.latitude;
            wellModel.longitude = formData.longitude;

            if (formData.externalContentTitle && formData.externalContentUrl &&
                formData.externalContentTitle.trim().length > 0 &&
                formData.externalContentUrl.trim().length > 0) {

                    wellModel.externalContent = [{
                        id: formData.externalContentID ?? -1,
                        createdBy: data.externalContent.length > 0 ? data.externalContent[0].createdBy : '',
                        title: formData.externalContentTitle.trim(),
                        url: formData.externalContentUrl.trim()
                    }];
            } else {
                wellModel.externalContent = [];
            }

            return wellModel;
                
    }

    return returnData;
}


export function shapeProtocolObjects(commData: IAcmDeviceProtocolObjectUsage | undefined): Array<IAcmProtocolObject> {

    const protocolObjects = commData?.protocolObjects.map(o => {
        o.acmObjectTypeText = AcmObjectTypeEnum[o.acmObjectType];

        o.hasPrimaryConnection = o.tcpIpPrimaryConnectionObject ? true : false;
        o.hasSecondaryConnection = o.tcpIpSecondaryConnectionObject ? true : false;

        if (o.tcpIpPrimaryConnectionObject) {
            const tcpipobj = o.tcpIpPrimaryConnectionObject;
            tcpipobj.ipAddress1 = tcpipobj.host1TcpIpAddress + "/" + tcpipobj.host1TcpIpPort;
            tcpipobj.ipAddress2 = tcpipobj.host2TcpIpAddress + "/" + tcpipobj.host2TcpIpPort;
        }

        if (o.tcpIpSecondaryConnectionObject) {
            const tcpipobj = o.tcpIpSecondaryConnectionObject;
            tcpipobj.ipAddress1 = tcpipobj.host1TcpIpAddress + "/" + tcpipobj.host1TcpIpPort;
            tcpipobj.ipAddress2 = tcpipobj.host2TcpIpAddress + "/" + tcpipobj.host2TcpIpPort;
        }

        if (o.primaryConnectionString !== null && o.primaryConnectionString.indexOf(':') !== -1) {
            o.ipAddress1 = o.primaryConnectionString.split(':')[0];
            o.port1 = parseInt(o.primaryConnectionString.split(':')[1]);
        }

        return o;
    }) ?? new Array<IAcmProtocolObject>();

    return protocolObjects;
}

export function shapeCommDisplayData(protocolObjects: Array<IAcmProtocolObject>): Array<CommDisplayInfo> {
    const commDisplayData = new Array<CommDisplayInfo>();

    protocolObjects.forEach(o => {

        if (o.tcpIpPrimaryConnectionObject) {

            const tcpipobj = o.tcpIpPrimaryConnectionObject;

            const item: CommDisplayInfo = {
                name: tcpipobj.displayText,
                ip: tcpipobj.host1TcpIpAddress,
                port: tcpipobj.host1TcpIpPort
            };

            commDisplayData.push(item);

            if ((tcpipobj.host2TcpIpAddress?.length ?? 0) > 0) {

                const item: CommDisplayInfo = {
                    name: tcpipobj.displayText,
                    ip: tcpipobj.host2TcpIpAddress,
                    port: tcpipobj.host2TcpIpPort
                };

                commDisplayData.push(item);
            }
        }

        if (o.tcpIpSecondaryConnectionObject) {

            const tcpipobj = o.tcpIpSecondaryConnectionObject;

            const item: CommDisplayInfo = {
                name: tcpipobj.displayText,
                ip: tcpipobj.host1TcpIpAddress,
                port: tcpipobj.host1TcpIpPort
            };

            commDisplayData.push(item);

            if ((tcpipobj.host2TcpIpAddress?.length ?? 0) > 0) {

                const item: CommDisplayInfo = {
                    name: tcpipobj.displayText,
                    ip: tcpipobj.host2TcpIpAddress,
                    port: tcpipobj.host2TcpIpPort
                };

                commDisplayData.push(item);
            }
        }

        if (o.tcpListenPrimaryConnectionObject) {

            const tcpobj = o.tcpListenPrimaryConnectionObject;

            const item: CommDisplayInfo = {
                name: tcpobj.displayText,
                ip: "Shared",
                port: undefined
            };

            commDisplayData.push(item);
        }

        if (o.tcpListenSecondaryConnectionObject) {

            const tcpobj = o.tcpListenSecondaryConnectionObject;

            const item: CommDisplayInfo = {
                name: tcpobj.displayText,
                ip: "Shared",
                port: undefined
            };

            commDisplayData.push(item);
        }
    });

    return commDisplayData;
}
